import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import DropdownFilter from '../../../../../input/DropdownFilter';
import { useStyles } from './Overview.styles';
import { useStyles as useCommonStyles } from '../ReportSubItems.styles';
import { BarChart } from '../../../../../layout/Charts';
import { InfoDisplay } from '../../../../../layout/InfoDisplay';
import { Loading } from '../../../../../layout/Loading';
import { ReportUnavailable } from '../../../../../layout/ReportUnavailable';
import { notifyError } from '../../../../../../utils/bugsnag/notifyError';
import {
  formatScale,
  formatToBarChart,
} from '../../../../../../utils/ChartUtils/chart-util';
import { getOverview } from '../../../../../../services/ReportService';

import {
  formatCurrencyDisplay,
  numberWithCommas,
} from '../../../../../../utils/FormatUtils/formatCurrencyValue';
import { useMenuContext } from '../../../../../../context/menuContext';
import {
  yearToDate,
  last12Months,
  thisWeek,
  lastMonth,
  last3Months,
} from '../../../../../../utils/date-utils';
import { DatePicker } from '../../../../../input/DatePicker/DatePicker';

export function Overview() {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const [filter, setFilter] = useState({ label: 'Year to date' });
  const [error, setError] = useState(false);
  const [errorRequestId, setErrorRequestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(true);
  const [typeFilter, setTypeFilter] = useState({
    delivery: true,
    takeout: true,
  });
  const [data, setData] = useState();
  const [params, setParams] = useState(yearToDate());
  const { selectedRestaurant } = useMenuContext();
  const filterBtnEl = useRef();
  const [popoverEl, setPopoverEl] = useState(null);

  useEffect(() => {
    if (filter.label !== 'Custom...') {
      const newParams = {
        'This week': thisWeek(),
        'Last month': lastMonth(),
        'Last 3 months': last3Months(),
        'Year to date': yearToDate(),
        '12 months': last12Months(),
      }[filter.label];
      if (newParams) setParams(newParams);
    }
  }, [filter]);

  useEffect(() => {
    setReloading(true);
    setError(false);
    setErrorRequestId(null);
    if (selectedRestaurant?.id && params)
      (async () => {
        try {
          const response = await getOverview(selectedRestaurant?.id, params);
          setData({
            ...response,
            chart: formatToBarChart(response.total.timeline),
          });
          setLoading(false);
          setReloading(false);
        } catch (err) {
          notifyError(err, { component: 'Overview', method: 'fetchOverview' });
          setErrorRequestId(err?.requestId || err?.__apiContext?.requestId || null);
          setError(true);
          setLoading(false);
          setReloading(false);
        }
      })();
  }, [params, selectedRestaurant]);

  const handleCheckbox = useCallback(
    (type) => {
      if (type === 'delivery') {
        setTypeFilter((old) => {
          if (!old.takeout && old.delivery)
            return { takeout: true, delivery: false };
          return { ...old, [type]: !old[type] };
        });
      }
      if (type === 'takeout') {
        setTypeFilter((old) => {
          if (old.takeout && !old.delivery)
            return { takeout: false, delivery: true };
          return { ...old, [type]: !old[type] };
        });
      }
    },
    [setTypeFilter]
  );

  const handlePopoverOpen = () => {
    setPopoverEl(filterBtnEl.current);
  };

  const handlePopoverClose = () => {
    setPopoverEl(null);
  };

  const activeFilter = useMemo(() => {
    if (typeFilter.delivery && typeFilter.takeout) return 'total';
    if (!typeFilter.delivery && typeFilter.takeout) return 'takeout';
    return 'delivery';
  }, [typeFilter]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ReportUnavailable requestId={errorRequestId} />;
  }

  return (
    <>
      <Box flex="1">
        <Box className={commonClasses.header}>
          <Typography variant="h1" className={commonClasses.pageTitle}>
            Business Summary
          </Typography>
          <Box className={classes.filterWrapper}>
            <Box display="flex" alignItems="center">
              <Checkbox
                color="primary"
                size="small"
                inputProps={{
                  'data-testid': 'delivery-checkbox',
                }}
                checked={typeFilter.delivery}
                onChange={() => handleCheckbox('delivery')}
              />
              <Typography variant="caption">Delivery</Typography>
              <Checkbox
                color="primary"
                size="small"
                inputProps={{
                  'data-testid': 'takeout-checkbox',
                }}
                checked={typeFilter.takeout}
                onChange={() => handleCheckbox('takeout')}
              />
              <Typography variant="caption">Takeout</Typography>
            </Box>
            <Box className={classes.dropdownGroup}>
              {selectedRestaurant?.timezone && (
                <Typography
                  className={classes.timezoneLabel}
                  data-testid="timezone-label"
                >
                  Times in {selectedRestaurant.timezone}
                </Typography>
              )}
              <Box ref={filterBtnEl}>
                <DropdownFilter
                  items={{
                    filters: [
                      'This week',
                      'Last month',
                      'Last 3 months',
                      'Year to date',
                      {
                        label: 'Custom...',
                        open: handlePopoverOpen,
                        close: handlePopoverClose,
                      },
                    ],
                    filter,
                    setFilter,
                  }}
                  className={classes.dropdownFilter}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {reloading ? (
          <Loading />
        ) : (
          <>
            <Box className={commonClasses.infoWrapper}>
              <InfoDisplay
                info={{
                  title: 'Gross Revenue',
                  info: formatCurrencyDisplay(data[activeFilter].revenue),
                }}
              />
              <InfoDisplay
                info={{
                  title: 'Net Margin',
                  info: formatCurrencyDisplay(
                    data[activeFilter].incomeFromWholesale
                  ),
                }}
              />
              <InfoDisplay
                info={{
                  title: 'Orders',
                  info: numberWithCommas(data[activeFilter].total_orders || 0),
                }}
                size={0.8}
              />
              <InfoDisplay
                info={{
                  title: 'Average Order Value',
                  info: formatCurrencyDisplay(data[activeFilter].average_ticket),
                }}
                size={0.8}
              />
            </Box>
            <Box className={commonClasses.infoWrapper}>
              <InfoDisplay
                info={{
                  title: 'Number of Customers',
                  info: numberWithCommas(data.total.customers_total),
                }}
                secondary
              />
              <InfoDisplay
                info={{
                  title: 'Returning Customers',
                  info: numberWithCommas(data.total.returning_customers.total),
                  percentage: String(
                    (data.total.returning_customers.percentage * 100).toFixed(0)
                  ),
                }}
                secondary
              />
              <InfoDisplay
                info={{
                  title: 'New Customers',
                  info: numberWithCommas(data.total.new_customers.total),
                  percentage: String(
                    (data.total.new_customers.percentage * 100).toFixed(0)
                  ),
                }}
                secondary
              />
            </Box>
            <Box
              className={commonClasses.infoContainer}
              style={{ overflowX: 'auto', maxWidth: 'calc(100VW - 30px)' }}
              marginBottom="24px"
            >
              <div className={classes.chartWrapper}>
                <BarChart
                  data={data.chart[activeFilter]}
                  keys={['Sales', 'Profits']}
                  colors={['#6854FF', '#FF82BA']}
                  index="month"
                  axisLeft={{
                    format: formatScale,
                  }}
                  margin={{ top: 70, right: 24, bottom: 32, left: 60 }}
                  borderRadius={{
                    top: {
                      left: 4,
                      right: 4,
                    },
                    bottom: {
                      left: 0,
                      right: 0,
                    },
                  }}
                />
              </div>
            </Box>
          </>
        )}
      </Box>
      <DatePicker
        popoverEl={popoverEl}
        handleClosePopover={handlePopoverClose}
        setParams={setParams}
      />
    </>
  );
}

Overview.propTypes = {};

export default Overview;
